import { ActivityRecord, useActivityFeed } from "../../pages/InspectionReviews/ReviewActivity";
import React, { useMemo, useState } from "react";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import "./FindingsTab.scss";

const displayStatus = (value) =>
  ({
    ReQuery: "Awaiting Client Response",
    InProgress: "In Progress",
    InReview: "In Review",
  }[value] ||
  value ||
  "Open");

const statusGroup = (value) => {
  if (value === "ReQuery") return "Awaiting Client Response";
  if (["InProgress", "InReview"].includes(value)) return "In Progress";
  return value || "Open";
};

const dateOnly = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const dateTime = (value) =>
  value
    ? new Date(value).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const initials = (value) =>
  String(value || "User")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const timestamp = (value) => (value ? new Date(value).getTime() : 0);

const statusOptions = [
  "All",
  "Open",
  "Awaiting Client Response",
  "In Progress",
  "Resolved",
  "Closed",
];

const FindingsTab = ({
  findings = [],
  observations = [],
  controls,
  sort = "newest",
  onDeleteObservation,
  onReviewObservation,
  canManageObservations = false,
  actionOpen,
  onToggleAction,
  onOpen,
  onEdit,
  onDelete,
  onCreate,
  createLabel = "Raise Finding",
  saving = false,
  showFilters = true,
  compact = false,
  tableOnly = false,
  hideTitle = false,
  alwaysShowTable = false,
  hideDateColumns = false,
  expanded = true,
  onToggleExpanded,
}) => {
  const combinedFindings = useMemo(() => [...findings, ...observations.map((item) => ({
    ...item, id: `observation-${item.id}`, observation: item, findingNumber: `OBS-${String(item.id).padStart(3, "0")}`,
    title: item.description, findingStatus: item.assessmentStatus, recordType: "Observation",
  }))], [findings, observations]);
  const inActivityFeed = useActivityFeed();
  const [expandedCards, setExpandedCards] = useState({});
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");

  const counts = useMemo(
    () =>
      combinedFindings.reduce(
        (result, finding) => {
          const group = statusGroup(finding.findingStatus);
          result[group] = (result[group] || 0) + 1;
          return result;
        },
        {
          total: combinedFindings.length,
          Open: 0,
          "Awaiting Client Response": 0,
          "In Progress": 0,
          Resolved: 0,
          Closed: 0,
        }
      ),
    [combinedFindings]
  );

  const visibleFindings = useMemo(() => {
    const term = showFilters ? search.trim().toLowerCase() : "";
    return combinedFindings
      .filter((finding) => {
        const matchesSearch =
          !term ||
          `${finding.findingNumber} ${finding.title} ${
            finding.descriptionPreview || ""
          } ${finding.category || ""} ${finding.raisedByName || ""} ${
            finding.responseRequestedFromUserName ||
            finding.responseRequestedFromRole ||
            ""
          }`
            .toLowerCase()
            .includes(term);
        const matchesStatus =
          !showFilters || status === "All" || statusGroup(finding.findingStatus) === status;
        return matchesSearch && matchesStatus;
      })
      .sort(
        (left, right) =>
          (sort === "oldest" ? 1 : -1) * (timestamp(controls ? left.createdOn : left.updatedOn || left.createdOn) - timestamp(controls ? right.createdOn : right.updatedOn || right.createdOn))
      );
  }, [combinedFindings, search, status, showFilters, sort, controls]);

  return (
    <section className={`findings-tab${tableOnly ? " findings-tab--checklist" : ""}${hideDateColumns ? " findings-tab--no-dates" : ""}`}>
      {!tableOnly && !(hideTitle && showFilters) && <header className="findings-tab__heading">
        {!hideTitle && !onToggleExpanded && <i className="bx bx-flag" />}
        <span>
          {!hideTitle && (onToggleExpanded ? (
            <button type="button" className="checklist-review__support-toggle" aria-expanded={expanded} aria-controls="checklist-findings-content" onClick={onToggleExpanded}>
              <i className={`bx bx-chevron-${expanded ? "down" : "right"}`} aria-hidden="true" />
              <b><i className="bx bx-flag" aria-hidden="true" /> Findings <span className="checklist-review__support-count">{findings.length}</span></b>
            </button>
          ) : <h2>Findings</h2>)}
          <p>Track observations, remediation, and closure for this question.</p>
        </span>
        {!showFilters && onCreate && <button type="button" className="findings-tab__raise piers-refresh-button" onClick={onCreate}>
          <i className="bx bx-plus" /> {createLabel}
        </button>}
      </header>}

      <div id={onToggleExpanded ? "checklist-findings-content" : undefined} hidden={!expanded}>
      {showFilters && <section className={`findings-tab__toolbar${tableOnly ? " findings-tab__toolbar--no-create" : ""}`}>
        {showFilters && <>
        {!compact && <label className="findings-tab__search">
          <i className="bx bx-search" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search findings..."
            aria-label="Search findings"
          />
        </label>}
        <nav className="question-status-filters" aria-label="Filter findings by status">
          {[...statusOptions, ...(observations.length ? ["Proposed", "Confirmed"] : [])].filter((option) => !compact || !["Closed", "Awaiting Client Response", "Resolved", "Confirmed"].includes(option)).map((option) => (
            <button
              type="button"
              key={option}
              className={status === option ? "active" : ""}
              onClick={() => setStatus(option)}
            >
              {option}
              <b>{option === "All" ? combinedFindings.length : counts[option] || 0}</b>
            </button>
          ))}
        </nav>
        </>}
        {controls}
        {!tableOnly && onCreate && <button
          type="button"
          className="findings-tab__raise piers-refresh-button"
          onClick={onCreate}
        >
          <i className="bx bx-plus" /> {createLabel}
        </button>}
      </section>}

      {compact ? (
        <ul className="checklist-review__simple-records" aria-label={observations.length ? "Observations and findings" : "Findings"}>
          {visibleFindings.map((finding) => (
            <ActivityRecord key={finding.id} id={`finding-${finding.id}`} type={finding.observation ? "Observation" : "Finding"} createdOn={finding.createdOn} title={finding.title || finding.descriptionPreview || finding.findingNumber} author={finding.raisedByName} status={displayStatus(finding.findingStatus)} listItem><li className="checklist-review__query-record finding-expandable-card">
              <button hidden={inActivityFeed} className="finding-expandable-card__header" type="button" aria-expanded={Boolean(expandedCards[finding.id])} aria-controls={`finding-card-${finding.id}`} onClick={() => setExpandedCards((current) => ({ ...current, [finding.id]: !current[finding.id] }))}>
                <span className="finding-expandable-card__summary"><b>{finding.findingNumber || finding.id}</b><small>{finding.recordType || "Finding"}{finding.raisedByName ? ` · ${finding.raisedByName}` : ""}</small>{!expandedCards[finding.id] && <span className="finding-expandable-card__preview">{finding.title || finding.descriptionPreview}</span>}</span>
                <em className="checklist-review__query-badge">{displayStatus(finding.findingStatus)}</em>
                <i className={`bx bx-chevron-${expandedCards[finding.id] ? "up" : "down"}`} aria-hidden="true" />
              </button>
              {(inActivityFeed || expandedCards[finding.id]) && <div id={`finding-card-${finding.id}`} className="finding-expandable-card__body">
                <strong>{finding.title}</strong>
                {finding.descriptionPreview && <p>{finding.descriptionPreview}</p>}
                <footer className="finding-expandable-card__actions">
                {finding.observation ? canManageObservations && <button type="button" className="question-query-refresh" disabled={saving} onClick={() => onReviewObservation(finding.observation)}>Review / Update</button> : <button type="button" className="question-query-refresh" onClick={() => onOpen(finding)}>View finding</button>}

              {(finding.observation ? canManageObservations : onEdit) && <button type="button" className="question-query-refresh" disabled={saving} aria-label={`Edit ${finding.findingNumber || finding.id}`} onClick={() => finding.observation ? onReviewObservation(finding.observation, "Edit") : onEdit(finding)}><i className="bx bx-edit-alt" aria-hidden="true" /> Edit</button>}
              {compact && (finding.observation ? onDeleteObservation && canManageObservations : onDelete) && <button type="button" className="question-query-refresh question-query-delete" disabled={saving} aria-label={`Delete ${finding.findingNumber || finding.id}`} onClick={() => finding.observation ? onDeleteObservation(finding.observation) : onDelete(finding)}><i className="bx bx-trash" /> Delete</button>}
              {finding.observation && canManageObservations && <Dropdown className="checklist-review__query-actions" inNavbar isOpen={actionOpen === finding.id} toggle={() => onToggleAction(actionOpen === finding.id ? null : finding.id)}><DropdownToggle color="link" aria-label={`Actions for observation ${finding.findingNumber}`}><i className="bx bx-dots-vertical-rounded" /></DropdownToggle><DropdownMenu end><DropdownItem onClick={() => { onToggleAction(null); onReviewObservation(finding.observation); }}>Review / Update</DropdownItem><DropdownItem onClick={() => { onToggleAction(null); onReviewObservation(finding.observation, "Finding"); }}>Convert to Finding</DropdownItem>{finding.findingStatus === "Proposed" && <DropdownItem onClick={() => { onToggleAction(null); onReviewObservation(finding.observation, "Closed"); }}>Close (Not an Observation or Finding)</DropdownItem>}</DropdownMenu></Dropdown>}
              {!finding.observation && <Dropdown className="checklist-review__query-actions" inNavbar isOpen={actionOpen === finding.id} toggle={() => onToggleAction(actionOpen === finding.id ? null : finding.id)}>
                <DropdownToggle color="link" aria-label={`Actions for finding ${finding.findingNumber || finding.id}`}><i className="bx bx-dots-vertical-rounded" aria-hidden="true" /></DropdownToggle>
                <DropdownMenu end className="checklist-review__query-record-menu">
                  <DropdownItem onClick={() => { onToggleAction(null); onOpen(finding); }}><i className="bx bx-show" aria-hidden="true" /> View</DropdownItem>
                  {onDelete && <DropdownItem onClick={() => { onToggleAction(null); onDelete(finding); }}>Delete finding</DropdownItem>}
                </DropdownMenu>
              </Dropdown>}
                </footer>
              </div>}
            </li></ActivityRecord>
          ))}
          {!visibleFindings.length && <li className="checklist-review__record-empty">{combinedFindings.length ? "No records match your filters." : onReviewObservation ? "No observations or findings for this question." : "No findings for this question."}</li>}
        </ul>
      ) : findings.length || tableOnly || alwaysShowTable ? (
        <section className="findings-tab__table" aria-label="Findings">
          <header>
            <span>ID</span>
            <span>Finding Title / Observation</span>
            <span>Severity</span>
            <span>Status</span>
            <span>Raised By</span>
            <span>Assigned To</span>
            {!hideDateColumns && <><span>Due Date</span><span>Last Updated</span></>}
            <span />
          </header>
          {visibleFindings.map((finding) => {
            const assignee =
              finding.responseRequestedFromUserName ||
              finding.responseRequestedFromRole ||
              "No action required";
            const currentStatus = displayStatus(finding.findingStatus);
            return (
              <article key={finding.id}>
                <button
                  type="button"
                  className="findings-tab__id"
                  onClick={() => onOpen(finding)}
                >
                  {finding.findingNumber}
                </button>
                <button
                  type="button"
                  className="findings-tab__title"
                  onClick={() => onOpen(finding)}
                >
                  <b>{finding.title}</b>
                  <small>
                    {finding.descriptionPreview ||
                      finding.category ||
                      "No observation summary provided."}
                  </small>
                </button>
                <span
                  className={`findings-tab__severity-value is-${String(
                    finding.severity || "low"
                  ).toLowerCase()}`}
                >
                  <i className="bx bx-flag" /> {finding.severity || "Low"}
                </span>
                <span
                  className={`findings-tab__status is-${String(
                    finding.findingStatus || "open"
                  ).toLowerCase()}`}
                >
                  {currentStatus}
                </span>
                <span className="findings-tab__person">
                  <i>{initials(finding.raisedByName || "Reviewer")}</i>
                  <span>
                    <b>{finding.raisedByName || "Reviewer"}</b>
                    <small>Reviewer</small>
                  </span>
                </span>
                <span className="findings-tab__person">
                  <i>{initials(assignee)}</i>
                  <span>
                    <b>{assignee}</b>
                    <small>
                      {finding.responseRequestedFromRole ||
                        (finding.managementResponseRequired
                          ? "Resolution owner"
                          : "—")}
                    </small>
                  </span>
                </span>
                {!hideDateColumns && <>
                <time
                  className={
                    finding.responseDueDate &&
                    timestamp(finding.responseDueDate) < Date.now() &&
                    !["Resolved", "Closed"].includes(finding.findingStatus)
                      ? "is-overdue"
                      : ""
                  }
                >
                  <i className="bx bx-calendar" />{" "}
                  {dateOnly(finding.responseDueDate)}
                </time>
                <time>{dateTime(finding.updatedOn || finding.createdOn)}</time>
                </>}
                <Dropdown
                  className="findings-tab__actions"
                  inNavbar
                  isOpen={actionOpen === finding.id}
                  toggle={() =>
                    onToggleAction(
                      actionOpen === finding.id ? null : finding.id
                    )
                  }
                >
                  <DropdownToggle
                    tag="button"
                    type="button"
                    disabled={saving}
                    aria-label={`Actions for ${finding.findingNumber}`}
                  >
                    <i className="bx bx-dots-vertical-rounded" />
                  </DropdownToggle>
                  <DropdownMenu end>
                    <DropdownItem onClick={() => onOpen(finding)}>
                      <i className="bx bx-show" /> Open finding
                    </DropdownItem>
                    <DropdownItem onClick={() => onEdit(finding)}>
                      <i className="bx bx-edit-alt" /> Edit finding
                    </DropdownItem>
                    {onDelete && <DropdownItem
                      className="is-danger"
                      onClick={() => onDelete(finding)}
                    >
                      <i className="bx bx-trash" /> Delete finding
                    </DropdownItem>}
                  </DropdownMenu>
                </Dropdown>
              </article>
            );
          })}
          {!visibleFindings.length && (
            <div className="findings-tab__no-results">
              <i className="bx bx-search" />
              <b>{findings.length ? "No matching findings" : "No findings for this question"}</b>
              <p>{findings.length ? "Try changing the search or filters." : "Findings raised for this question will appear here."}</p>
            </div>
          )}
        </section>
      ) : showFilters ? (
        <div className="findings-tab__empty">
          <i className="bx bx-flag" />
          <b>No findings for this question</b>
          <p>
            Raise a finding to record an observation and track its resolution.
          </p>
          {onCreate && <button type="button" onClick={onCreate}>
            <i className="bx bx-plus" /> {createLabel}
          </button>}
        </div>
      ) : null}
      </div>
    </section>
  );
};

export default FindingsTab;
